// Short-lived, memory-only cache. No playback URLs, credentials or errors are persisted.
export const createReadCache = ({ scope, limit = 128, now = Date.now }) => {
  const entries = new Map();
  let currentScope;
  let generation = 0;
  const clear = () => {
    generation++;
    entries.clear();
  };
  const syncScope = () => {
    const next = scope();
    if (next !== currentScope) {
      clear();
      currentScope = next;
    }
  };
  const read = (key, loader, ttl) => {
    syncScope();
    const entry = entries.get(key);
    if (entry && (entry.pending || entry.expires > now())) return entry.promise;
    entries.delete(key);
    const version = generation;
    const next = { pending: true, expires: 0 };
    next.promise = Promise.resolve()
      .then(loader)
      .then((value) => {
        syncScope();
        if (generation !== version)
          throw Object.assign(new Error("Read context changed"), {
            code: "STALE_CONTEXT"
          });
        next.pending = false;
        next.expires = now() + ttl;
        return value;
      })
      .catch((error) => {
        if (entries.get(key) === next) entries.delete(key);
        throw error;
      });
    entries.set(key, next);
    while (entries.size > limit) entries.delete(entries.keys().next().value);
    return next.promise;
  };
  return { read, clear };
};

export const getReadScope = () => {
  // Comparing the stored profile also catches changes to parental preferences.
  return ["userToken", "profile", "language", "sessionid"]
    .map((key) => localStorage.getItem(key) || "")
    .join("\n");
};

export const readCache = createReadCache({ scope: getReadScope });

export const createRequestQueue = (concurrency = 4) => {
  let active = 0;
  const queue = [];
  const release = () => {
    active--;
    drain();
  };
  const drain = () => {
    while (active < concurrency && queue.length) {
      const { run, resolve, reject } = queue.shift();
      active++;
      Promise.resolve()
        .then(run)
        .then(resolve, reject)
        .then(release);
    }
  };
  return (run) =>
    new Promise((resolve, reject) => {
      queue.push({ run, resolve, reject });
      drain();
    });
};
